import React, { useEffect, useState, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import Button from 'components/shared/Button';
import Alert from 'components/shared/Alert';

import savedArrangements from 'domains/saved_arrangements';

import session from 'domains/session';
import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';

import EditArrangementModal from './EditArrangementModal';

function CustomArrangementsTools() {
  const dispatch = useAppDispatch();
  const activeCanvasId = useAppSelector(canvases.selectors.activeId);
  const canvasIds = useAppSelector(canvases.selectors.selectIds);
  const configuredArrangements = useAppSelector(savedArrangements.selectors.forSave);
  const activeSubcatalog = useAppSelector(activeEditor.selectors.subcatalog);
  const activeArrangement = useAppSelector(savedArrangements.selectors.active);
  const isLoggedIn = useAppSelector(session.selectors.isLoggedIn);
  const isCartProduct = useAppSelector(activeEditor.selectors.isCartProduct);

  const [shouldPreventDialog, setShouldPreventDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [showEditDialog, setShowEditDialog] = useState(false);

  const preventDialogLocalStorageKey = 'preventRemoveProductDialog';

  // Has the user requested we don't show the confirm dialog
  useEffect(() => {
    const stored = localStorage.getItem(preventDialogLocalStorageKey);
    const should = !(stored === null || stored !== 'true');
    setShouldPreventDialog(should);
  }, []);

  function handleAddProduct() {
    dispatch(canvases.actions.createCanvas({ duplicate: true }));
  }

  function handleRemoveProduct() {
    if (activeCanvasId) {
      dispatch(canvases.actions.removeCanvas(activeCanvasId));
    }
  }

  function handleRemoveProductClick() {
    if (!shouldPreventDialog) {
      setShowConfirmDialog(true);
    } else {
      handleRemoveProduct();
    }
  }

  // Set if user checked the "don't show again" checkbox
  function handlePreventDialogChange(checked: boolean) {
    localStorage.setItem(preventDialogLocalStorageKey, checked.toString());
    setShouldPreventDialog(checked);
  }

  const shouldShowArrangementButton = useMemo(() => {
    return !isCartProduct && activeSubcatalog && activeSubcatalog.arrangement;
  }, [isCartProduct, activeSubcatalog]);

  return (
    <React.Fragment>
      <Button
        type="icon"
        a11yText="Add Product"
        icon="arrangementsAdd"
        onClick={handleAddProduct}
        disabled={!shouldShowArrangementButton}
        size="xl"
        tooltipProps={{
          placement: 'left',
        }}
      />
      <Button
        type="icon"
        id="remove-custom-arrangement-product"
        a11yText="Remove Product"
        icon="arrangementsRemove"
        disabled={canvasIds.length <= 1}
        onClick={handleRemoveProductClick}
        size="xl"
        tooltipProps={{
          placement: 'left',
        }}
      />
      <Button
        type="icon"
        icon="arrangementsSave"
        a11yText="Save Custom Arrangement"
        size="xl"
        id="save-custom-arrangement-button"
        className="save-custom-arrangement-button"
        onClick={() => setShowEditDialog(true)}
        disabled={
          !isLoggedIn || canvasIds.length <= 1 || canvasIds.length !== configuredArrangements.length
        }
        tooltipProps={{
          placement: 'left',
        }}
      />

      {showConfirmDialog && (
        <Alert
          title="Confirm Remove Product"
          labelledBy="confirm-remove-product-alert"
          lastFocusedElementId="remove-custom-arrangement-product"
          message="Are you sure you want to remove this product?"
          showPreventDialog
          onPreventDialog={(e) => handlePreventDialogChange(e.target.checked)}
          onContinue={() => {
            setShowConfirmDialog(false);
            handleRemoveProduct();
          }}
          onClose={() => setShowConfirmDialog(false)}
          onCancel={() => setShowConfirmDialog(false)}
        />
      )}

      {showEditDialog && (
        <EditArrangementModal
          onClose={() => setShowEditDialog(false)}
          arrangement={activeArrangement}
        />
      )}
    </React.Fragment>
  );
}

export default CustomArrangementsTools;
